import { GAME_COPY } from './gameText.js';
import type { Lang } from './text.js';

/**
 * What a guest sees when the room says no.
 *
 * The room answers in its own words ("the brig does not vote", "an anomaly is already open").
 * Those are for the log and the other clients, and they stay as they are. Here they become:
 *
 * - `silent`: a harmless double tap, or something the guest cannot act on. Nothing shows.
 * - `private`: a turned-down sabotage. It goes only onto the saboteur's own game (games/Hack.tsx),
 *   never into a banner a neighbour could read, because only a saboteur can get one.
 * - `toast`: a short, plain message in the top banner.
 *
 * Anything this list does not know becomes "Something went wrong — try again."
 */
export type ErrorRoute = { kind: 'silent' } | { kind: 'private'; text: string } | { kind: 'toast'; text: string };

interface ErrorCopy {
  votedOut: string;
  notNow: string;
  notInMeeting: string;
  meetingOn: string;
  meetingLocked: string;
  cannotPick: string;
  notYourself: string;
  voteClosed: string;
  votingOnYou: string;
  someoneElseFixing: string;
  finishFirst: string;
  jobFull: string;
  messageSaved: string;
  writeFirst: string;
  showEnded: string;
  somethingWrong: string;
}

const en: ErrorCopy = {
  votedOut: 'You’re voted out until the next era.',
  notNow: 'Not now — wait for the next era.',
  notInMeeting: 'Not during a meeting.',
  meetingOn: 'A meeting is already on.',
  meetingLocked: 'The vote comes after the alarms and the meeting.',
  cannotPick: 'That guest can’t be picked right now.',
  notYourself: 'You can’t pick yourself.',
  voteClosed: 'That vote has closed.',
  votingOnYou: 'The table is voting on you — you can’t vote.',
  someoneElseFixing: 'Someone else is fixing this alarm.',
  finishFirst: 'Finish your own game first, then you can watch others.',
  jobFull: 'That job just filled up — pick another.',
  messageSaved: 'You’ve already saved your message.',
  writeFirst: 'Write something first.',
  showEnded: 'Tonight’s show has ended. Thanks for coming!',
  somethingWrong: 'Something went wrong — try again.',
};

const no: ErrorCopy = {
  votedOut: 'Du er stemt ut til neste tidsalder.',
  notNow: 'Ikke nå – vent til neste tidsalder.',
  notInMeeting: 'Ikke under et møte.',
  meetingOn: 'Et møte er allerede i gang.',
  meetingLocked: 'Avstemningen kommer etter alarmene og møtet.',
  cannotPick: 'Den gjesten kan ikke velges nå.',
  notYourself: 'Du kan ikke velge deg selv.',
  voteClosed: 'Den avstemningen er over.',
  votingOnYou: 'Bordet stemmer om deg – du kan ikke stemme.',
  someoneElseFixing: 'Noen andre fikser denne alarmen.',
  finishFirst: 'Fullfør ditt eget spill først, så kan du se på andre.',
  jobFull: 'Den jobben ble nettopp full – velg en annen.',
  messageSaved: 'Du har allerede lagret meldingen.',
  writeFirst: 'Skriv noe først.',
  showEnded: 'Kveldens forestilling er over. Takk for at du kom!',
  somethingWrong: 'Noe gikk galt – prøv igjen.',
};

const ERROR_COPY: Record<Lang, ErrorCopy> = { en, no };

/** Refusals that only a saboteur's own game can get (games/Hack.tsx). */
const SABOTAGE_ONLY: Record<string, 'busy' | 'soon' | 'none' | 'notNow' | 'over'> = {
  'an anomaly is already open': 'busy',
  'the drive is still settling — try again shortly': 'soon',
  'no sabotage left this era': 'none',
  'the machines already have the ship': 'notNow',
  'the games are over for this stop': 'over',
  'not until the games start': 'notNow',
};

/** Double taps and internals: nothing a guest needs to hear about. */
const SILENT = new Set([
  'it is not with you',
  'already passed',
  'not this game',
  'nobody to pass to',
  'no table game right now',
  // A guest voted out tapping PLANT: their card already says they sit this one out.
  'not at the table right now',
  'pass or plant',
  'you cannot watch yourself',
  'wrong chapter',
  'that station belongs to another post',
  'you have not taken a post yet',
  'unknown vote',
  'vote yes or no',
  'seat not claimed',
  'nothing happens',
  'not a guest',
  'missing seat token',
]);

const TOAST: Record<string, keyof ErrorCopy> = {
  'the brig does not vote': 'votedOut',
  'you are in the brig': 'votedOut',
  'the brig cannot call a meeting': 'votedOut',
  'not while the ship is between eras': 'notNow',
  'not while the ship is jumping': 'notNow',
  'not while the table is in a meeting': 'notInMeeting',
  'a meeting is already under way': 'meetingOn',
  'meetings open when an alarm shows where it came from': 'meetingLocked',
  'the meeting opens by itself after the alarms': 'meetingLocked',
  'pick someone else': 'notYourself',
  'nobody by that call sign': 'cannotPick',
  'choose a guest for the table to vote on': 'cannotPick',
  'you cannot name yourself': 'notYourself',
  'that meeting is over': 'voteClosed',
  'the table is voting on you': 'votingOnYou',
  'someone else is on this alarm': 'someoneElseFixing',
  'finish your own station first': 'finishFirst',
  'empty seed': 'writeFirst',
  'this voyage is sealed': 'showEnded',
};

/**
 * Route one refusal. `intent` is the kind of message the room turned down, when the phone knows
 * it (the cloud room echoes an id; the Mac's local room does not, and then the phone's last
 * message is the best guess).
 */
export function routeRoomError(raw: string, intent: string | null, lang: Lang): ErrorRoute {
  const e = ERROR_COPY[lang];
  const g = GAME_COPY[lang];
  const error = raw.trim();

  const sabotage = SABOTAGE_ONLY[error];
  if (intent === 'sabotage' || sabotage) {
    const text =
      sabotage === 'busy'
        ? g.sabotageBusy
        : sabotage === 'soon'
          ? g.sabotageTooSoon
          : sabotage === 'none'
            ? g.sabotageNoneLeft
            : sabotage === 'over'
              ? g.sabotageOver
              : g.sabotageNotNow;
    return { kind: 'private', text };
  }

  // A second tap on PLANT at the table is harmless; a second SAVE MY MESSAGE deserves a word.
  if (error === 'already planted') {
    return intent === 'plantSeed' ? { kind: 'toast', text: e.messageSaved } : { kind: 'silent' };
  }
  if (SILENT.has(error) || error.startsWith('malformed')) return { kind: 'silent' };
  if (/ is fully crewed$/.test(error) || /^callsign .+ is taken$/.test(error)) return { kind: 'toast', text: e.jobFull };

  const key = TOAST[error];
  if (key) return { kind: 'toast', text: e[key] };
  return { kind: 'toast', text: e.somethingWrong };
}
